import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Eye, EyeOff, Loader2, Building2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success("Benvenuto in Neapolis Finance");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const demoLogin = async () => {
    setError("");
    setBusy(true);
    try {
      await login("admin@neapolis.it", "neapolis123");
      toast.success("Accesso demo effettuato");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#080C14] relative overflow-hidden">
      {/* ambient glow */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px]" />

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-14 relative z-10 border-r border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-slate-950" />
          </div>
          <div>
            <p className="font-display font-extrabold text-xl tracking-tight text-slate-50">Neapolis</p>
            <p className="text-xs text-slate-400 uppercase tracking-widest">Finance Suite</p>
          </div>
        </div>

        <div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight text-slate-50">
            Ogni euro delle tue strutture,<br />
            <span className="text-amber-500">sempre sotto controllo.</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base max-w-md leading-relaxed">
            Gestisci entrate e uscite di tutti i tuoi B&B Neapolis. Il totale complessivo è
            sempre visibile, in tempo reale.
          </p>
          <div className="mt-10 flex items-center gap-8">
            <div>
              <p className="font-mono text-2xl font-bold text-emerald-400">€ Live</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Bilancio in tempo reale</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="flex items-center gap-2 text-slate-300">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              <span className="text-sm">Grafici & Budget</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-600">© 2026 Neapolis Finance — Gestione B&B</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-[#111A2E]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-2xl"
        >
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-slate-950" />
            </div>
            <p className="font-display font-extrabold text-lg text-slate-50">Neapolis Finance</p>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-50">
            {mode === "login" ? "Bentornato" : "Crea il tuo account"}
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            {mode === "login" ? "Accedi per gestire le tue strutture" : "Inizia a tracciare le tue finanze"}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Nome</label>
                <input
                  data-testid="input-name"
                  className="mt-1.5 w-full bg-[#0E1524] border border-white/15 text-slate-100 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 text-sm placeholder:text-slate-500"
                  placeholder="Mario Rossi"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Email</label>
              <input
                data-testid="input-email"
                type="email"
                className="mt-1.5 w-full bg-[#0E1524] border border-white/15 text-slate-100 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 text-sm placeholder:text-slate-500"
                placeholder="tu@neapolis.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Password</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="input-password"
                  type={showPw ? "text" : "password"}
                  className="w-full bg-[#0E1524] border border-white/15 text-slate-100 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 text-sm placeholder:text-slate-500"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="auth-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="btn-submit-auth"
              type="submit"
              disabled={busy}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2.5 rounded-lg shadow-md hover:shadow-amber-500/20 transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Accedi" : "Registrati"}
            </button>
          </form>

          <button
            data-testid="btn-demo-login"
            onClick={demoLogin}
            disabled={busy}
            className="mt-3 w-full bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium px-4 py-2.5 rounded-lg border border-white/10 transition-all disabled:opacity-60"
          >
            Prova la Demo
          </button>

          <p className="mt-6 text-center text-sm text-slate-400">
            {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button
              data-testid="btn-toggle-mode"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-amber-500 hover:text-amber-400 font-medium"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
